import * as Sentry from '@sentry/react'

export const GRAPHQL_ERROR_MESSAGE = 'GraphQL Error'

type HeadersLike = Headers | Record<string, string> | Array<[string, string]> | undefined

export function hasHeader(headers: HeadersLike, name: string): boolean {
  if (!headers) return false
  const lower = name.toLowerCase()
  if (headers instanceof Headers) {
    return headers.has(name)
  }
  if (Array.isArray(headers)) {
    for (const [key] of headers) {
      if (key.toLowerCase() === lower) return true
    }
    return false
  }
  if (typeof headers === 'object') {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === lower) return true
    }
  }
  return false
}

export function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  // Guard for environments where Request/URL may be undefined (e.g., some test runners)
  type GlobalCtors = typeof globalThis & { Request?: typeof Request; URL?: typeof URL }
  const g = globalThis as GlobalCtors
  const hasRequestCtor = typeof g.Request !== 'undefined'
  const hasURLCtor = typeof g.URL !== 'undefined'
  if (hasRequestCtor && g.Request && input instanceof g.Request) return (input as Request).url
  if (hasURLCtor && g.URL && input instanceof g.URL) return (input as URL).toString()
  return String(input)
}

export function getRequestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  type GlobalCtors = typeof globalThis & { Request?: typeof Request }
  const g = globalThis as GlobalCtors
  const hasRequestCtor = typeof g.Request !== 'undefined'
  if (hasRequestCtor && g.Request && input instanceof g.Request) return (input as Request).method
  return init?.method ?? 'GET'
}

export function getRequestHeaders(input: RequestInfo | URL, init?: RequestInit): HeadersLike {
  type GlobalCtors = typeof globalThis & { Request?: typeof Request }
  const g = globalThis as GlobalCtors
  const hasRequestCtor = typeof g.Request !== 'undefined'
  if (hasRequestCtor && g.Request && input instanceof g.Request) return (input as Request).headers
  return init?.headers as HeadersLike
}

const NON_REPORTING_HOSTS = ['api.segment.io', 'segment.com', 'posthog.com']

function isNonReportingHost(url: string): boolean {
  try {
    const hostname = new URL(url).hostname
    return NON_REPORTING_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))
  } catch {
    return false
  }
}

export function setupSentryMonitoring(): void {
  if (process.env.NODE_ENV !== 'production') {
    return
  }

  Sentry.init({
    dsn: 'https://d600de818c7e427f9b8cdde5983484d5@o174923.ingest.sentry.io/5343478',
    environment: process.env.REACT_APP_TARGET_ENV,
    release: process.env.REACT_APP_VERSION,
    // Password manager extensions (LastPass, Bitwarden, etc.) reject promises with this message when they can't find form elements to auto-fill.
    ignoreErrors: [/Object Not Found Matching Id/],
    beforeSend(event) {
      if (event.fingerprint) {
        return event
      }
      const exceptionEntry = event.exception?.values?.[0]
      const type = exceptionEntry?.type ?? ''
      const value = exceptionEntry?.value ?? ''
      if (type === 'ApolloError' || value === GRAPHQL_ERROR_MESSAGE) {
        return { ...event, fingerprint: ['apollo-error', type, value] }
      }
      return event
    },
  })

  // Global fetch wrapper: report any non-Apollo 4xx/5xx responses to Sentry
  const originalFetch: typeof window.fetch = window.fetch.bind(window)

  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [input, init] = args
    const url = getRequestUrl(input)
    const method = getRequestMethod(input, init)
    // Identify Apollo-originated GraphQL requests so we can avoid double-reporting
    const headers = getRequestHeaders(input, init)
    const isApolloGraphQL = hasHeader(headers, 'Apollo-Require-Preflight')
    try {
      const response = await originalFetch(...args)
      try {
        if (
          (response.status >= 400 || response.status === 0) &&
          isApolloGraphQL === false &&
          !isNonReportingHost(url)
        ) {
          Sentry.withScope((scope) => {
            if (response.status === 0) {
              scope.setLevel('error')
              scope.setTag('cors', 'blocked_or_opaque')
            } else {
              scope.setLevel(response.status >= 500 ? 'error' : 'warning')
            }
            scope.setTag('http', 'fetch')
            scope.setExtra('url', url)
            scope.setExtra('method', method)
            scope.setExtra('status', response.status)
            scope.setExtra('statusText', response.statusText)
            Sentry.captureException(
              new Error(
                response.status === 0
                  ? `HTTP status 0 (CORS/network blocked) at ${url}`
                  : `${response.status >= 500 ? 'HTTP server error' : 'HTTP client error'} ${
                      response.status
                    } at ${url}`,
              ),
            )
          })
        }
      } catch {
        // best-effort reporting; never break fetch
      }
      return response
    } catch (err: unknown) {
      try {
        if (isApolloGraphQL === false && !isNonReportingHost(url)) {
          Sentry.addBreadcrumb({
            type: 'http',
            category: 'fetch',
            level: 'error',
            data: {
              url,
              method,
              reason: err instanceof Error ? err.message : String(err),
            },
          })
        }
      } catch {
        // best-effort breadcrumb; never break fetch
      }
      return Promise.reject(err)
    }
  }
}
